import { NumberedTileMeta } from "game-common/models"
import { Rectangle, Texture } from "pixi.js"
import { compositeArtPixels } from "./sprites/composite_art_pixels"
import { contentTextureGeneration } from "./content_textures"
import { SlotAtlas } from "./sprites/slot_atlas"

/** An irregular wet edge and a translucent submerged foot, baked into shared atlas pages once. */
export const submergedRockPixels = (source: ArrayLike<number>, width: number, height: number, waterline: number): Uint8ClampedArray => {
    const rgba = Uint8ClampedArray.from(source)
    const water = [36, 91, 115]
    for (let x = 0; x < width; x++) {
        const edge = waterline + Math.round(Math.sin(x * .73) * .8 + Math.sin(x * 1.91) * .45)
        for (let y = Math.max(0, edge - 2); y < height; y++) {
            const i = (y * width + x) * 4
            if (!rgba[i + 3]) continue
            if (y < edge) {
                for (let c = 0; c < 3; c++) rgba[i + c] = rgba[i + c] * .66 + water[c] * .12
            } else {
                const fade = Math.min(1, (y - edge + 1) / Math.max(2, height - edge))
                for (let c = 0; c < 3; c++) rgba[i + c] = rgba[i + c] * .38 + water[c] * .62
                rgba[i + 3] *= .55 * (1 - fade)
                if (y === edge && (x * 13 + y * 7) % 17 < 3) {
                    rgba[i] = 115; rgba[i + 1] = 169; rgba[i + 2] = 174; rgba[i + 3] = 190
                }
            }
        }
    }
    return rgba
}

const atlas = new SlotAtlas(128)
const cache = new Map<string, Texture>()
let generation = -1

export const submergedRockTexture = (tileset: string, composite: string, columns: number,
    children: NumberedTileMeta[], waterline: number): Texture | undefined => {
    const next = contentTextureGeneration()
    if (generation !== next) { cache.clear(); atlas.reset(); generation = next }
    const line = Math.round(waterline / 2) * 2
    const key = `${tileset}:${composite}:${line}`
    const cached = cache.get(key)
    if (cached && !cached.baseTexture.destroyed) return cached
    const art = compositeArtPixels(tileset, columns, children)
    if (!art || art.width > atlas.size || art.height > atlas.size) return undefined
    const slot = atlas.claim(), context = slot && atlas.context(slot)
    if (!slot || !context) return undefined
    const rgba = submergedRockPixels(art.data, art.width, art.height, line)
    context.putImageData(new ImageData(rgba, art.width, art.height), slot.x, slot.y)
    atlas.touch(slot)
    const texture = new Texture(slot.texture.baseTexture, new Rectangle(slot.x, slot.y, art.width, art.height))
    cache.set(key, texture)
    return texture
}
